// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Print utilities for SRM reports.
 */

import { escapeHtml, wrapHtmlDocument, PRINT_COLORS } from '@/applications/shared'
import type { SrmFinding, SrmRequest } from '../types'
import {
  byDecisionRelevance,
  citation,
  criteriaSummary,
  type CriterionStatus,
  type Determination,
  type EligibilityCriterion,
} from './eligibility'

export { openPrintWindow } from '@/applications/shared'

/** One document's eligibility determination and its full criteria checklist. */
export interface SrmReportEligibility {
  determination: Determination | null
  criteria: EligibilityCriterion[]
}

export interface SrmReportTrack {
  agentId: string
  label: string
  icon: string
  findings: SrmFinding[]
  /**
   * The track's eligibility determinations, for tracks that gate on one. Printed above the
   * findings: the terms findings exist only because a determination cleared them, so the
   * verdict is what makes the findings list — an empty one included — mean anything.
   */
  eligibility?: SrmReportEligibility[]
}

function getSrmRiskLevel(score: number | null): { label: string; color: string } {
  if (score === null) return { label: 'Unknown', color: PRINT_COLORS.inkMuted }
  if (score <= 2) return { label: 'Low', color: PRINT_COLORS.low.text }
  if (score <= 3) return { label: 'Moderate', color: PRINT_COLORS.medium.text }
  return { label: 'High', color: PRINT_COLORS.high.text }
}

function computeAggregatedScore(findings: SrmFinding[]): number | null {
  const scores = findings.map((f) => f.riskScore).filter((s): s is number => s !== null)
  if (scores.length === 0) return null
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
}

/** SRM-specific print styles (layered on top of shared PRINT_BASE_STYLES) */
const SRM_EXTRA_STYLES = `
  /* SRM overrides */
  h1 { margin-bottom: 0.5rem; }
  h2 { margin: 2rem 0 1rem; padding-bottom: 0.5rem; border-bottom: 1px solid ${PRINT_COLORS.borderLight}; }

  .meta { color: ${PRINT_COLORS.inkMuted}; font-size: 0.9rem; margin-bottom: 2rem; }

  .summary {
    position: static;
    background: ${PRINT_COLORS.surfaceMuted};
    border: 1px solid ${PRINT_COLORS.border};
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
  .summary::before { display: none; }
  .summary-title { font-weight: 600; margin-bottom: 1rem; }
  .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
  .summary-item { text-align: center; }
  .summary-icon { font-size: 1.5rem; }
  .summary-label { font-size: 0.75rem; color: ${PRINT_COLORS.inkLight}; text-transform: uppercase; letter-spacing: 0.05em; }
  .summary-value { font-size: 1.25rem; font-weight: 600; }
  /* A verdict is a phrase, not a score — it needs room the numeric tiles do not. */
  .summary-value-verdict { font-size: 0.95rem; line-height: 1.3; }

  /* A track carrying a full criteria checklist runs past one page; the findings and criteria
     inside it avoid breaking individually. */
  .track { margin-bottom: 2rem; }
  .track-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; }
  .track-icon { font-size: 1.25rem; }

  .finding {
    position: static;
    border: 1px solid ${PRINT_COLORS.border};
    border-radius: 6px;
    padding: 1rem 1.25rem;
    margin-bottom: 0.75rem;
    background: ${PRINT_COLORS.surface};
    page-break-inside: avoid;
  }
  .finding::before { display: none; }

  .finding-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.75rem; gap: 1rem; }
  .finding-header-left { flex: 1; min-width: 0; }
  .finding-kb-id { font-size: 0.75rem; color: ${PRINT_COLORS.inkLight}; font-family: monospace; margin-bottom: 0.25rem; }
  .finding-title { font-weight: 600; font-size: 1rem; }
  .finding-header-right { display: flex; gap: 0.75rem; flex-shrink: 0; }
  .finding-badge { text-align: center; }
  .finding-badge-value {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .finding-badge-label { font-size: 0.6rem; color: ${PRINT_COLORS.inkLight}; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 0.15rem; }
  .finding-badge-kb { background: ${PRINT_COLORS.surfaceMuted}; border: 1px solid ${PRINT_COLORS.border}; color: ${PRINT_COLORS.ink}; }
  .finding-badge-exposure-critical { background: ${PRINT_COLORS.high.bg}; border: 1px solid ${PRINT_COLORS.high.border}; color: ${PRINT_COLORS.high.text}; }
  .finding-badge-exposure-high { background: ${PRINT_COLORS.medium.bg}; border: 1px solid ${PRINT_COLORS.medium.border}; color: ${PRINT_COLORS.medium.text}; }
  .finding-badge-exposure-low { background: ${PRINT_COLORS.low.bg}; border: 1px solid ${PRINT_COLORS.low.border}; color: ${PRINT_COLORS.low.text}; }

  .finding-content { font-size: 0.9rem; color: ${PRINT_COLORS.inkMuted}; margin-bottom: 0.5rem; }

  .finding-exposure { font-size: 0.85rem; margin-bottom: 0.5rem; padding: 0.5rem 0.75rem; border-radius: 4px; }
  .finding-exposure-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 0.25rem; }

  .finding-source { background: ${PRINT_COLORS.surfaceMuted}; border-radius: 4px; padding: 0.75rem; font-size: 0.85rem; margin-bottom: 0.5rem; }
  .finding-source-label { font-size: 0.7rem; color: ${PRINT_COLORS.inkLight}; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem; }
  .finding-source-quote { font-style: italic; border-left: 2px solid ${PRINT_COLORS.border}; padding-left: 0.75rem; }
  .finding-source-ref { font-size: 0.75rem; color: ${PRINT_COLORS.inkLight}; margin-top: 0.25rem; }

  .finding-recommendation { font-size: 0.85rem; margin-top: 0.5rem; padding: 0.5rem 0.75rem; background: ${PRINT_COLORS.surfaceMuted}; border-radius: 4px; }
  .finding-recommendation-label { font-size: 0.7rem; color: ${PRINT_COLORS.inkLight}; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 0.25rem; }

  .no-findings { color: ${PRINT_COLORS.inkMuted}; font-style: italic; }

  .subhead {
    font-size: 0.7rem;
    color: ${PRINT_COLORS.inkLight};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    margin: 1.5rem 0 0.75rem;
  }

  .determination {
    position: static;
    border-radius: 6px;
    padding: 1rem 1.25rem;
    margin-bottom: 1rem;
    page-break-inside: avoid;
  }
  .determination::before { display: none; }
  .determination-header { display: flex; align-items: baseline; gap: 0.5rem; }
  .determination-verdict { font-weight: 600; }
  .determination-counts { margin-left: auto; font-size: 0.75rem; font-weight: 600; flex-shrink: 0; }
  .determination-rationale { font-size: 0.9rem; color: ${PRINT_COLORS.ink}; margin-top: 0.5rem; }
  .determination-recommendation { font-size: 0.9rem; font-weight: 600; color: ${PRINT_COLORS.ink}; margin-top: 0.5rem; }
  .determination-quote { font-size: 0.85rem; font-style: italic; color: ${PRINT_COLORS.inkMuted}; margin-top: 0.5rem; }
  .determination-quote-ref { font-style: normal; font-size: 0.75rem; color: ${PRINT_COLORS.inkLight}; }

  .criterion {
    position: static;
    border: 1px solid ${PRINT_COLORS.border};
    border-radius: 6px;
    padding: 0.6rem 1rem;
    margin-bottom: 0.4rem;
    background: ${PRINT_COLORS.surface};
    page-break-inside: avoid;
  }
  .criterion::before { display: none; }
  .criterion-absent { background: ${PRINT_COLORS.surfaceMuted}; }
  .criterion-header { display: flex; align-items: center; gap: 0.5rem; }
  .criterion-title { font-size: 0.9rem; font-weight: 500; min-width: 0; }
  .criterion-status {
    margin-left: auto;
    flex-shrink: 0;
    display: inline-block;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.03em;
  }
  .criterion-reason { font-size: 0.85rem; color: ${PRINT_COLORS.inkMuted}; margin-top: 0.5rem; }
  .criterion-recommendation {
    font-size: 0.85rem;
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: ${PRINT_COLORS.surfaceMuted};
    border-radius: 4px;
  }
  .criterion-recommendation-label { font-size: 0.7rem; color: ${PRINT_COLORS.inkLight}; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; margin-bottom: 0.25rem; }
`

export interface SrmReportData {
  request: SrmRequest
  tracks: SrmReportTrack[]
  includeRiskSummary: boolean
}

/** Background/border/text triple from PRINT_COLORS, used to tone a banner or badge. */
interface PrintTone {
  bg: string
  border: string
  text: string
}

const NEUTRAL_TONE: PrintTone = {
  bg: PRINT_COLORS.surfaceMuted,
  border: PRINT_COLORS.border,
  text: PRINT_COLORS.ink,
}

const VERDICT_PRESENTATION: Record<string, { tone: PrintTone; label: string }> = {
  'EXPEDITED-ELIGIBLE': { tone: PRINT_COLORS.low, label: 'Expedited Eligible' },
  'NOT ELIGIBLE - ROUTE TO FULL SRM': {
    tone: PRINT_COLORS.high,
    label: 'Not Eligible — Route to Full SRM',
  },
  'INSUFFICIENT INFORMATION': { tone: PRINT_COLORS.medium, label: 'Insufficient Information' },
}

function verdictPresentation(verdict: string): { tone: PrintTone; label: string } {
  return VERDICT_PRESENTATION[verdict.toUpperCase()] ?? { tone: NEUTRAL_TONE, label: verdict }
}

const STATUS_TONE: Record<CriterionStatus, PrintTone> = {
  PRESENT: PRINT_COLORS.high,
  UNCLEAR: PRINT_COLORS.medium,
  ABSENT: PRINT_COLORS.low,
}

function renderCriterion(criterion: EligibilityCriterion): string {
  const tone = STATUS_TONE[criterion.status]
  return `
    <div class="criterion${criterion.status === 'ABSENT' ? ' criterion-absent' : ''}">
      <div class="criterion-header">
        <div class="criterion-title">${escapeHtml(criterion.knowledgeItem)}</div>
        <span class="criterion-status" style="background: ${tone.bg}; border: 1px solid ${tone.border}; color: ${tone.text}">${criterion.status}</span>
      </div>
      ${criterion.reason ? `<div class="criterion-reason">${escapeHtml(criterion.reason)}</div>` : ''}
      ${criterion.recommendation ? `
        <div class="criterion-recommendation">
          <div class="criterion-recommendation-label">Recommendation</div>
          <div>${escapeHtml(criterion.recommendation)}</div>
        </div>
      ` : ''}
    </div>
  `
}

function renderDeterminationBanner(
  determination: Determination,
  criteria: EligibilityCriterion[],
): string {
  const { tone, label } = verdictPresentation(determination.verdict)
  const ref = citation(determination)
  return `
    <div class="determination" style="background: ${tone.bg}; border: 1px solid ${tone.border}; color: ${tone.text}">
      <div class="determination-header">
        <span class="determination-verdict">${escapeHtml(label)}</span>
        <span class="determination-counts">${escapeHtml(criteriaSummary(criteria))}</span>
      </div>
      ${determination.rationale ? `<div class="determination-rationale">${escapeHtml(determination.rationale)}</div>` : ''}
      ${determination.recommendation ? `<div class="determination-recommendation">${escapeHtml(determination.recommendation)}</div>` : ''}
      ${determination.decisiveText ? `
        <div class="determination-quote">
          "${escapeHtml(determination.decisiveText)}"${ref ? `<span class="determination-quote-ref"> · ${escapeHtml(ref)}</span>` : ''}
        </div>
      ` : ''}
    </div>
  `
}

function renderEligibility(entry: SrmReportEligibility): string {
  const { determination, criteria } = entry
  if (!determination && criteria.length === 0) {
    return '<p class="no-findings">No determination recorded.</p>'
  }
  const banner = determination ? renderDeterminationBanner(determination, criteria) : ''
  return banner + byDecisionRelevance(criteria).map(renderCriterion).join('')
}

/** The track's determination, from whichever of its documents carries one. */
function trackDetermination(track: SrmReportTrack): Determination | null {
  return track.eligibility?.map((e) => e.determination).find((d) => d != null) ?? null
}

/** Generate printable HTML for SRM report */
export function generateSrmReportHTML(data: SrmReportData): string {
  const { request, tracks, includeRiskSummary } = data

  // Build risk summary section
  let summaryHTML = ''
  if (includeRiskSummary) {
    const summaryItems = tracks.map((track) => {
      // A gated track's headline is its verdict, not a score: its findings cover only the half
      // of the review the determination cleared, so averaging them understates the outcome.
      const determination = trackDetermination(track)
      let value: { text: string; color: string; verdict: boolean }
      if (determination) {
        const { tone, label } = verdictPresentation(determination.verdict)
        value = { text: label, color: tone.text, verdict: true }
      } else {
        const score = computeAggregatedScore(track.findings)
        const risk = getSrmRiskLevel(score)
        value = { text: `${score !== null ? `${score}/5` : '—'} (${risk.label})`, color: risk.color, verdict: false }
      }
      return `
        <div class="summary-item">
          <div class="summary-icon">${track.icon}</div>
          <div class="summary-label">${escapeHtml(track.label)}</div>
          <div class="summary-value${value.verdict ? ' summary-value-verdict' : ''}" style="color: ${value.color}">
            ${escapeHtml(value.text)}
          </div>
        </div>
      `
    }).join('')

    summaryHTML = `
      <div class="summary">
        <div class="summary-title">Risk Summary</div>
        <div class="summary-grid">${summaryItems}</div>
      </div>
    `
  }

  // Build track sections
  const tracksHTML = tracks.map((track) => {
    const { findings } = track
    const findingsHTML = findings.length === 0
      ? '<p class="no-findings">No findings identified.</p>'
      : findings.map((f) => {
          const exposureDesc = f.exposure?.replace(/^(NEGLIGIBLE|LOW|MODERATE|HIGH|SEVERE|CRITICAL):\s*/i, '') ?? ''
          const exposureBadgeClass = f.exposureLevel === 'CRITICAL' || f.exposureLevel === 'SEVERE'
            ? 'finding-badge-exposure-critical'
            : f.exposureLevel === 'HIGH' || f.exposureLevel === 'MODERATE'
              ? 'finding-badge-exposure-high'
              : 'finding-badge-exposure-low'
          return `
            <div class="finding">
              <div class="finding-header">
                <div class="finding-header-left">
                  <div class="finding-kb-id">KB-${escapeHtml(f.kbItemId)}${f.sectionReference ? ` · ${escapeHtml(f.sectionReference)}` : ''}</div>
                  <div class="finding-title">${escapeHtml(f.content || `Knowledge Base Item ${f.kbItemId}`)}</div>
                </div>
                <div class="finding-header-right">
                  ${f.riskScore !== null ? `
                    <div class="finding-badge">
                      <div class="finding-badge-value finding-badge-kb">${f.riskScore}/5</div>
                      <div class="finding-badge-label">KB Risk</div>
                    </div>
                  ` : ''}
                  ${f.exposureLevel ? `
                    <div class="finding-badge">
                      <div class="finding-badge-value ${exposureBadgeClass}">${escapeHtml(f.exposureLevel)}</div>
                      <div class="finding-badge-label">AI Assessment</div>
                    </div>
                  ` : ''}
                </div>
              </div>
              ${exposureDesc ? `
                <div class="finding-exposure" style="background: ${f.exposureLevel === 'CRITICAL' || f.exposureLevel === 'SEVERE' ? PRINT_COLORS.high.bg : f.exposureLevel === 'HIGH' || f.exposureLevel === 'MODERATE' ? PRINT_COLORS.medium.bg : PRINT_COLORS.low.bg}; border: 1px solid ${f.exposureLevel === 'CRITICAL' || f.exposureLevel === 'SEVERE' ? PRINT_COLORS.high.border : f.exposureLevel === 'HIGH' || f.exposureLevel === 'MODERATE' ? PRINT_COLORS.medium.border : PRINT_COLORS.low.border}">
                  <div class="finding-exposure-label">Exposure</div>
                  <div>${escapeHtml(exposureDesc)}</div>
                </div>
              ` : ''}
              ${f.sourceText ? `
                <div class="finding-source">
                  <div class="finding-source-label">Source</div>
                  <div class="finding-source-quote">"${escapeHtml(f.sourceText)}"</div>
                  ${f.sectionReference ? `<div class="finding-source-ref">${escapeHtml(f.sectionReference)}${f.pageNumber ? ` · Page ${f.pageNumber}` : ''}</div>` : ''}
                </div>
              ` : ''}
              ${f.recommendation ? `
                <div class="finding-recommendation">
                  <div class="finding-recommendation-label">Recommendation</div>
                  <div>${escapeHtml(f.recommendation)}</div>
                </div>
              ` : ''}
            </div>
          `
        }).join('')

    // A gated track prints its determination first and then labels the findings half —
    // unlabelled, the criteria checklist and the terms findings read as one flat list.
    const eligibilityHTML = track.eligibility?.length
      ? track.eligibility.map(renderEligibility).join('')
        + '<div class="subhead">Vendor Terms</div>'
      : ''

    return `
      <div class="track">
        <div class="track-header">
          <span class="track-icon">${track.icon}</span>
          <h2>${escapeHtml(track.label)}</h2>
        </div>
        ${eligibilityHTML}
        ${findingsHTML}
      </div>
    `
  }).join('')

  const content = `
    <h1>Software Risk Management Report</h1>
    <div class="meta">
      <div><strong>Request:</strong> ${escapeHtml(request.groupId)}</div>
      ${request.externalRef ? `<div><strong>External ID:</strong> ${escapeHtml(request.externalRef)}</div>` : ''}
      ${request.title ? `<div><strong>Title:</strong> ${escapeHtml(request.title)}</div>` : ''}
      ${request.uploadedBy ? `<div><strong>Submitted by:</strong> ${escapeHtml(request.uploadedBy)}</div>` : ''}
      <div><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
      <div><strong>Documents:</strong> ${request.documents.length}</div>
    </div>
    ${summaryHTML}
    ${tracksHTML}
  `

  return wrapHtmlDocument(`SRM Report - ${request.groupId}`, content, SRM_EXTRA_STYLES)
}
